import { HelpModal } from '@components'

import { Flex, Link, Spacing, Text } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { DocumentDetailsCard } from '@features/invoices/elements/DocumentDetailsCard'
import { EInvoiceStatusTag } from '@features/invoices/elements/EInvoiceStatusTag'
import { getEInvoiceStatusConfig } from '@features/invoices/utils/eInvoices/getEInvoiceStatusConfig'

import { useOpenHelpCenter } from '../../../../../../../../hooks/useOpenHelpCenter'
import { useInvoice } from '../../../../contexts/invoiceContext'
import { EInvoiceCommentSection } from './elements/EInvoiceCommentSection'
import * as Styled from './styles'

export const EInvoiceDetailsCard = (): ReactElement | null => {
  const { t } = useTranslation()
  const { invoice, isFetching, isLoading } = useInvoice()
  const { isHelpCenterEnabled, openHelp } = useOpenHelpCenter()

  const handleContactSupportClick = useCallback(() => {
    openHelp()
  }, [openHelp])

  const {
    eInvoiceComment: comment = null,
    eInvoiceReasonCode: reasonCode = null,
    eInvoiceStatus = null,
  } = invoice?.status ?? {}

  if (!isLoading && !eInvoiceStatus) {
    return null
  }

  const config = getEInvoiceStatusConfig(eInvoiceStatus)

  if (!config) {
    return null
  }

  const {
    detailedDescriptionTKey,
    icon,
    labelTKey,
    shortDescriptionColor,
    shortDescriptionTKey,
    shouldShowContactSupport = false,
    shouldShowReasonCode = true,
    tagColor,
  } = config

  const shouldShowShortDescription = !!shortDescriptionTKey
  const shouldShowComment = !!comment
  const shouldShowDetailedDescription = !!detailedDescriptionTKey
  const shouldShowCodeReason = shouldShowReasonCode && !!reasonCode
  const shouldShowContactSupportModal = shouldShowContactSupport && !isHelpCenterEnabled

  return (
    <>
      {shouldShowContactSupportModal && <HelpModal />}

      <DocumentDetailsCard
        isLoading={isFetching}
        title={t('external_invoices.view.sidebar.einvoice_details_card.title')}
      >
        <Styled.EInvoiceDetailsCardHeader>
          <Flex flexDirection="column" gap={Spacing.S8}>
            <Flex.Item alignSelf="flex-start">
              <EInvoiceStatusTag color={tagColor} icon={icon} label={t(labelTKey)} />
            </Flex.Item>

            {shouldShowShortDescription && (
              <Text color={shortDescriptionColor} variant="bodySM">
                {t(shortDescriptionTKey)}
              </Text>
            )}

            {shouldShowContactSupport && (
              <Link colorVariant="highlight" onClick={handleContactSupportClick}>
                {t('contact.support')}
              </Link>
            )}
          </Flex>
        </Styled.EInvoiceDetailsCardHeader>

        {shouldShowComment && (
          <DocumentDetailsCard.Entry
            title={t('external_invoices.view.sidebar.einvoice_details_card.comment_label')}
            titleIcon="messageCircle"
          >
            <EInvoiceCommentSection comment={comment} />
          </DocumentDetailsCard.Entry>
        )}

        {shouldShowDetailedDescription && (
          <DocumentDetailsCard.Entry
            title={t('external_invoices.view.sidebar.einvoice_details_card.description_label')}
          >
            <Text variant="bodySM">{t(detailedDescriptionTKey)}</Text>
          </DocumentDetailsCard.Entry>
        )}

        {shouldShowCodeReason && (
          <DocumentDetailsCard.Entry
            title={t('external_invoices.view.sidebar.einvoice_details_card.code_reason_label')}
          >
            <Text variant="bodySM">{reasonCode}</Text>
          </DocumentDetailsCard.Entry>
        )}
      </DocumentDetailsCard>
    </>
  )
}
